'use client'

// Next Imports
import Link from 'next/link'
import { useRouter } from 'next/navigation'

// Third-party Imports
import * as Icon from 'lucide-react'
import { ArrowLeftIcon, FileTextIcon, HouseIcon, PieChartIcon, SearchIcon } from 'lucide-react'

// Component Imports
import { Button } from '@/components/ui/button'

// Util Imports
import { cn } from '@/lib/utils'

const ORBIT = [
  {
    key: 'search',
    Glyph: SearchIcon,
    tint: 'text-muted-foreground',
    at: '-top-4 -left-24 lg:-left-32',
    delay: '0s',
    duration: '7s'
  },
  {
    key: 'companies',
    Glyph: Icon.Building2Icon,
    tint: 'text-blue-600 dark:text-blue-400',
    at: '-top-10 -right-16 lg:-right-24',
    delay: '-2.4s',
    duration: '8s'
  },
  {
    key: 'reports',
    Glyph: PieChartIcon,
    tint: 'text-muted-foreground',
    at: 'top-20 -right-28 lg:-right-40',
    delay: '-4.1s',
    duration: '6.5s'
  },
  {
    key: 'notes',
    Glyph: FileTextIcon,
    tint: 'text-teal-600 dark:text-teal-400',
    at: 'bottom-0 -left-32 lg:-left-44',
    delay: '-1.2s',
    duration: '7.5s'
  }
]

const NotFoundView = () => {
  const router = useRouter()

  return (
    <div className='flex min-h-full flex-1 flex-col items-center justify-center gap-10 px-6 py-16'>
      <div className='flex flex-col items-center gap-6'>
        <div className='relative'>
          <div
            aria-hidden
            className='bg-muted absolute top-1/2 left-1/2 -z-20 size-72 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl sm:size-96'
          />

          <div
            aria-hidden
            className='absolute -inset-x-20 -inset-y-12 -z-10 [mask-image:radial-gradient(closest-side,black,transparent)] opacity-25'
            style={{
              backgroundImage: 'radial-gradient(currentColor 1.5px, transparent 1.5px)',
              backgroundSize: '18px 18px',
              color: 'var(--muted-foreground)'
            }}
          />

          <p className='from-primary via-primary to-primary/65 bg-linear-to-br bg-clip-text text-[7rem] leading-none font-bold tracking-tighter text-transparent tabular-nums sm:text-[10rem]'>
            404
          </p>

          {ORBIT.map(({ key, Glyph, tint, at, delay, duration }) => (
            <span
              key={key}
              aria-hidden
              style={{ animationDelay: delay, animationDuration: duration }}
              className={cn(
                'bg-card animate-chip-drift absolute hidden items-center justify-center rounded-xl border p-2.5 shadow-sm motion-reduce:animate-none sm:flex',
                at
              )}
            >
              <Glyph className={cn('size-5', tint)} />
            </span>
          ))}
        </div>

        <div className='flex max-w-md flex-col items-center gap-3 text-center'>
          <h1 className='text-3xl font-semibold tracking-tight sm:text-4xl'>Page not found</h1>
          <p className='text-muted-foreground text-pretty'>
            We couldn&rsquo;t find the page you were looking for. It may have been moved or deleted, or the link may be
            incorrect.
          </p>
        </div>

        <div className='flex flex-col gap-3 sm:flex-row'>
          <Button size='lg' nativeButton={false} render={<Link href='/' />}>
            <HouseIcon />
            Back to dashboard
          </Button>

          <Button size='lg' variant='outline' onClick={() => router.back()}>
            <ArrowLeftIcon />
            Go back
          </Button>
        </div>
      </div>
    </div>
  )
}

export default NotFoundView
